import React, {useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {InstrumentedCenteredWaveLoader} from '../utility/InstrumentedLoader';
import {useWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {
  SAVE_WORKSPACE_TEMPLATE_DESCRIPTION,
  UPDATE_WORKSPACE_TEMPLATE_DESCRIPTION,
} from './constants';
import {useTemplateSections} from './useTemplateSections';
import {
  WorkspaceTemplateErrorBanner,
  WorkspaceTemplateInfoBanner,
} from './WorkspaceTemplateBanners';
import {WorkspaceTemplateContentFooter} from './WorkspaceTemplateContentFooter';
import {WorkspaceTemplateContentHeader} from './WorkspaceTemplateContentHeader';
import {WorkspaceTemplateDisplayValues} from './WorkspaceTemplateDisplayValues';
import {useWorkspaceTemplateDrawerContext} from './WorkspaceTemplateDrawerContextProvider';
import {WorkspaceTemplateRow} from './WorkspaceTemplateRow';
import {WorkspaceTemplateUpdatedAt} from './WorkspaceTemplateUpdatedAt';

export const WorkspaceTemplateContent = () => {
  const {
    isLoadingWorkspaceTemplates,
    workspaceTemplateMode,
    workspaceTemplates,
    workspaceTemplatesError,
  } = useWorkspaceTemplateDrawerContext();
  const {linePlot: currWorkspaceLinePlotSettings} =
    useWorkspaceSettingsContext();

  // This is safe to do since we only support 1 template per user within an org
  // and it's enforced in the sql database table as well.
  const workspaceTemplate = workspaceTemplates[0];
  const lastUpdatedAt = workspaceTemplate?.updatedAt;

  const [error, setError] = useState<string>();

  const templateSections = useTemplateSections({
    workspaceTemplate,
    currWorkspaceLinePlotSettings,
  });

  return (
    <div
      className="flex w-full flex-col"
      data-test="workspace-template-content">
      <WorkspaceTemplateContentHeader />
      <div className={twMerge('flex flex-col', 'p-16', 'h-full w-full')}>
        <p
          className={twMerge(
            'text-base leading-[140%] text-moon-800',
            'mb-20'
          )}>
          {workspaceTemplateMode === 'save'
            ? SAVE_WORKSPACE_TEMPLATE_DESCRIPTION
            : UPDATE_WORKSPACE_TEMPLATE_DESCRIPTION}
        </p>
        {(error || workspaceTemplatesError) && (
          <WorkspaceTemplateErrorBanner
            workspaceTemplateMode={workspaceTemplateMode}
          />
        )}
        <h2
          className={twMerge(
            'text-base font-semibold leading-[22px]',
            'mb-10 pb-10',
            'border-b-[1px] border-b-moon-250',
            'flex items-center gap-8'
          )}>
          Settings <WorkspaceTemplateUpdatedAt updatedAt={lastUpdatedAt} />
        </h2>
        <WorkspaceTemplateRows
          isLoadingWorkspaceTemplates={isLoadingWorkspaceTemplates}
        />
        <WorkspaceTemplateDisplayValues templateSections={templateSections} />
        {!isLoadingWorkspaceTemplates && <WorkspaceTemplateInfoBanner />}
      </div>
      <WorkspaceTemplateContentFooter setError={setError} />
    </div>
  );
};

const WorkspaceTemplateRows = ({
  isLoadingWorkspaceTemplates,
}: {
  isLoadingWorkspaceTemplates: boolean;
}) => {
  if (isLoadingWorkspaceTemplates) {
    return (
      <div className="h-[5rem]">
        <InstrumentedCenteredWaveLoader
          name="workspace-templates"
          size="small"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-20">
      <WorkspaceTemplateRow label="Line plot settings" />
    </div>
  );
};
